import React, { useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { goBackOrHome } from '../../navigation/goBackOrHome';
import { MaterialIcons } from '@expo/vector-icons';
import { useAppDispatch, useAppSelector } from '../../store/store';
import {
  logoutUser,
  getUserStats,
  deleteAccount,
  selectUser,
  selectIsUpdatingProfile,
  selectAuthError,
} from './authSlice';
import { BrandLogo } from '../../components';
import { fpColors } from '../../styles/theme';
import { logger } from '../../utils/logger';
import type { UserStats } from '../../api/types';

/**
 * Profile — Fidelity Purple redesign. Mirrors the pattern used by
 * Item Detail / Share Item: custom top bar, hero avatar card, stat
 * bento, grouped setting rows, and a destructive-action group.
 * Native stack header is disabled on this route (AppNavigator).
 */
export const ProfileScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();

  const user = useAppSelector(selectUser);
  const profileBusy = useAppSelector(selectIsUpdatingProfile);
  const authError = useAppSelector(selectAuthError);

  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    logger.info('👤 Profile: Loading profile screen');
    (async () => {
      setStatsLoading(true);
      try {
        const r = await dispatch(getUserStats());
        if (getUserStats.fulfilled.match(r)) setUserStats(r.payload);
      } catch (err) {
        logger.error('📊 Profile: Failed to load user stats', err);
      } finally {
        setStatsLoading(false);
      }
    })();
  }, [dispatch]);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      const result = await dispatch(logoutUser({ callBackend: true }));
      if (logoutUser.fulfilled.match(result)) {
        navigation.reset({ index: 0, routes: [{ name: 'Login' }] });
      }
    } finally {
      setLoggingOut(false);
    }
  };

  const confirmDelete = async () => {
    setShowDeleteModal(false);
    const r = await dispatch(deleteAccount());
    if (deleteAccount.fulfilled.match(r)) {
      dispatch(logoutUser({ callBackend: false }));
    }
  };

  if (!user) {
    return (
      <View style={[styles.page, { alignItems: 'center', justifyContent: 'center' }]}>
        <Text style={styles.stateText}>No user data available.</Text>
      </View>
    );
  }

  const initials = (user.full_name || 'U')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('') || 'U';

  const memberSince = userStats
    ? new Date(userStats.created_at).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
      })
    : null;

  return (
    <View style={styles.page}>
      {/* Top bar */}
      <View style={styles.topBar}>
        <View style={styles.topBarInner}>
          <Pressable
            onPress={() => goBackOrHome(navigation)}
            hitSlop={8}
            style={styles.topBtn}
            accessibilityRole="button"
            accessibilityLabel="Back"
          >
            <MaterialIcons name="arrow-back" size={22} color={fpColors.onSurface} />
          </Pressable>
          <View style={styles.topBrand}>
            <BrandLogo variant="horizontal" height={22} tone="color" />
          </View>
          <Pressable
            onPress={() => navigation.navigate('Feedback')}
            hitSlop={8}
            style={styles.topBtn}
            accessibilityRole="button"
            accessibilityLabel="Send feedback"
          >
            <MaterialIcons
              name="chat-bubble-outline"
              size={20}
              color={fpColors.onSurface}
            />
          </Pressable>
        </View>
      </View>

      <ScrollView
        contentContainerStyle={styles.scroll}
        showsVerticalScrollIndicator={false}
      >
        {/* Hero avatar card */}
        <View style={styles.heroCard}>
          <View style={styles.avatar}>
            <Text style={styles.avatarText}>{initials}</Text>
            <View
              style={[
                styles.verifyBadge,
                user.is_verified && styles.verifyBadgeOk,
              ]}
            >
              <MaterialIcons
                name={user.is_verified ? 'verified' : 'error-outline'}
                size={14}
                color={user.is_verified ? '#fff' : fpColors.error}
              />
            </View>
          </View>
          <Text style={styles.userName} numberOfLines={1}>
            {user.full_name}
          </Text>
          <Text style={styles.userEmail} numberOfLines={1}>
            {user.email}
          </Text>
          <View style={styles.badgeRow}>
            <View
              style={[
                styles.statusPill,
                user.is_verified ? styles.statusPillOk : styles.statusPillWarn,
              ]}
            >
              <Text
                style={[
                  styles.statusPillText,
                  {
                    color: user.is_verified
                      ? fpColors.primary
                      : fpColors.error,
                  },
                ]}
              >
                {user.is_verified ? 'VERIFIED' : 'UNVERIFIED'}
              </Text>
            </View>
          </View>
        </View>

        {/* Stats bento.
         *
         * Per backend#320 (closed wontfix 2026-05-28): there is no
         * UserSession table on BE. `userStats.active_tokens` counts
         * unconsumed OTPs (email-verify / password-reset), NOT
         * sessions. Rendering it as "Active sessions" was a misread
         * of the field name + would always show 0/1 anyway.
         *
         * The actual security primitive (logout-all via token_version
         * bump) is preserved via the "Sign out of all devices" CTA
         * below — no security regression from dropping the tile.
         *
         * Brought back when: multi-device user tickets justify the
         * UserSession table + Manage Sessions sheet investment.
         */}
        <View style={styles.statsGrid}>
          <StatTile
            icon="event-available"
            label="Member since"
            value={memberSince || '—'}
            tone={fpColors.secondary}
          />
        </View>

        {/* Profile settings group */}
        <View style={styles.groupCard}>
          <Text style={styles.groupHeader}>PROFILE</Text>
          <SettingRow
            icon="person-outline"
            title="Edit profile"
            subtitle="Update your name and other details"
            onPress={() => navigation.navigate('EditProfile')}
            loading={profileBusy}
          />
          <SettingRow
            icon="lock-outline"
            title="Change password"
            subtitle="Update your account password"
            onPress={() => navigation.navigate('ChangePassword')}
          />
          <SettingRow
            icon="mail-outline"
            title="Change email"
            subtitle="Update your email address"
            onPress={() => navigation.navigate('ChangeEmail')}
          />
        </View>

        {/* Account group */}
        <View style={styles.groupCard}>
          <Text style={styles.groupHeader}>ACCOUNT</Text>
          <SettingRow
            icon="chat-bubble-outline"
            title="Send feedback"
            subtitle="Tell us what you think"
            onPress={() => navigation.navigate('Feedback')}
          />
          <SettingRow
            icon="logout"
            title="Log out"
            subtitle="Sign out of your account"
            onPress={handleLogout}
            showChevron={false}
            loading={loggingOut}
          />
          <SettingRow
            icon="delete-outline"
            title="Delete account"
            subtitle="Permanently remove your account"
            onPress={() => setShowDeleteModal(true)}
            showChevron={false}
            danger
          />
        </View>

        {authError ? (
          <View style={styles.errorBanner}>
            <MaterialIcons
              name="error-outline"
              size={16}
              color={fpColors.error}
            />
            <Text style={styles.errorText}>{authError}</Text>
          </View>
        ) : null}

        <Text style={styles.versionText}>Founsi.ai · v1.0.0</Text>
      </ScrollView>

      {/* Delete confirm modal */}
      <Modal
        visible={showDeleteModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowDeleteModal(false)}
      >
        <View style={styles.scrim}>
          <Pressable
            style={styles.scrimTap}
            onPress={() => setShowDeleteModal(false)}
          />
          <View style={styles.confirmCard}>
            <View style={styles.confirmIcon}>
              <MaterialIcons name="warning" size={22} color={fpColors.error} />
            </View>
            <Text style={styles.confirmTitle}>Delete account</Text>
            <Text style={styles.confirmBody}>
              This permanently removes your account, inventory, and shares.
              This action cannot be undone.
            </Text>
            <View style={styles.confirmActions}>
              <Pressable
                style={({ pressed }) => [
                  styles.confirmSecondary,
                  pressed && { opacity: 0.85 },
                ]}
                onPress={() => setShowDeleteModal(false)}
              >
                <Text style={styles.confirmSecondaryLabel}>Cancel</Text>
              </Pressable>
              <Pressable
                style={({ pressed }) => [
                  styles.confirmDanger,
                  pressed && { opacity: 0.85 },
                ]}
                onPress={confirmDelete}
              >
                <MaterialIcons
                  name="delete-outline"
                  size={16}
                  color={fpColors.onPrimary}
                />
                <Text style={styles.confirmDangerLabel}>Delete</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

/* ——— local pieces ——— */

const StatTile: React.FC<{
  icon: keyof typeof MaterialIcons.glyphMap;
  label: string;
  value: string;
  tone: string;
}> = ({ icon, label, value, tone }) => (
  <View style={styles.statTile}>
    <View style={[styles.statIconWrap, { backgroundColor: `${tone}1a` }]}>
      <MaterialIcons name={icon} size={16} color={tone} />
    </View>
    <Text style={styles.statValue} numberOfLines={1}>
      {value}
    </Text>
    <Text style={styles.statLabel}>{label}</Text>
  </View>
);

const SettingRow: React.FC<{
  icon: keyof typeof MaterialIcons.glyphMap;
  title: string;
  subtitle?: string;
  onPress: () => void;
  showChevron?: boolean;
  loading?: boolean;
  danger?: boolean;
}> = ({
  icon,
  title,
  subtitle,
  onPress,
  showChevron = true,
  loading = false,
  danger = false,
}) => (
  <Pressable
    onPress={loading ? undefined : onPress}
    style={({ pressed }) => [
      styles.settingRow,
      pressed && !loading && styles.settingRowPressed,
      loading && { opacity: 0.6 },
    ]}
    accessibilityRole="button"
    accessibilityLabel={title}
  >
    <View
      style={[
        styles.settingIcon,
        danger && styles.settingIconDanger,
      ]}
    >
      {loading ? (
        <ActivityIndicator
          size="small"
          color={danger ? fpColors.error : fpColors.primary}
        />
      ) : (
        <MaterialIcons
          name={icon}
          size={18}
          color={danger ? fpColors.error : fpColors.primary}
        />
      )}
    </View>
    <View style={{ flex: 1 }}>
      <Text
        style={[
          styles.settingTitle,
          danger && { color: fpColors.error },
        ]}
        numberOfLines={1}
      >
        {title}
      </Text>
      {subtitle ? (
        <Text style={styles.settingSubtitle} numberOfLines={1}>
          {subtitle}
        </Text>
      ) : null}
    </View>
    {showChevron && !loading ? (
      <MaterialIcons
        name="chevron-right"
        size={20}
        color={fpColors.outline}
      />
    ) : null}
  </Pressable>
);

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: fpColors.background },
  topBar: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
    paddingTop: Platform.OS === 'ios' ? 48 : 12,
  },
  topBarInner: {
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    gap: 10,
  },
  topBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  topBrand: { flex: 1, alignItems: 'center' },

  scroll: { padding: 16, gap: 16, paddingBottom: 30 },

  heroCard: {
    alignItems: 'center',
    padding: 20,
    borderRadius: 18,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    gap: 6,
  },
  avatar: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: fpColors.primaryContainer,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 6,
  },
  avatarText: {
    fontFamily: 'Inter_800ExtraBold',
    fontSize: 28,
    color: fpColors.onPrimaryContainer,
    letterSpacing: 0.5,
  },
  verifyBadge: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 2,
    borderColor: fpColors.surfaceContainerLowest,
    justifyContent: 'center',
    alignItems: 'center',
  },
  verifyBadgeOk: { backgroundColor: fpColors.primary },
  userName: {
    fontFamily: 'Inter_800ExtraBold',
    fontSize: 20,
    color: fpColors.onSurface,
    letterSpacing: -0.2,
  },
  userEmail: {
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
  },
  badgeRow: { flexDirection: 'row', gap: 8, marginTop: 6 },
  statusPill: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
  },
  statusPillOk: {
    backgroundColor: `${fpColors.primary}12`,
    borderColor: `${fpColors.primary}33`,
  },
  statusPillWarn: {
    backgroundColor: `${fpColors.error}10`,
    borderColor: `${fpColors.error}33`,
  },
  statusPillText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 10,
    letterSpacing: 0.6,
  },

  statsGrid: { flexDirection: 'row', gap: 10 },
  statTile: {
    flex: 1,
    borderRadius: 14,
    padding: 12,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    gap: 6,
  },
  statIconWrap: {
    width: 30,
    height: 30,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  statValue: {
    fontFamily: 'Inter_800ExtraBold',
    fontSize: 18,
    color: fpColors.onSurface,
    letterSpacing: -0.3,
  },
  statLabel: {
    fontFamily: 'Inter_500Medium',
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },

  groupCard: {
    padding: 6,
    borderRadius: 16,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  groupHeader: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
    letterSpacing: 0.8,
    paddingHorizontal: 10,
    paddingTop: 8,
    paddingBottom: 4,
  },
  settingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 10,
    borderRadius: 12,
  },
  settingRowPressed: {
    backgroundColor: fpColors.surfaceContainerLow,
  },
  settingIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: `${fpColors.primary}14`,
    justifyContent: 'center',
    alignItems: 'center',
  },
  settingIconDanger: {
    backgroundColor: `${fpColors.error}14`,
  },
  settingTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  settingSubtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    marginTop: 1,
  },

  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderRadius: 10,
    backgroundColor: fpColors.errorContainer,
  },
  errorText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.onErrorContainer,
  },
  stateText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
  },
  versionText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 11,
    color: fpColors.outline,
    textAlign: 'center',
    marginTop: 4,
  },

  /* ——— confirm modal ——— */
  scrim: {
    flex: 1,
    backgroundColor: 'rgba(31, 26, 34, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  scrimTap: { ...StyleSheet.absoluteFillObject },
  confirmCard: {
    width: '100%',
    maxWidth: 360,
    padding: 22,
    borderRadius: 18,
    backgroundColor: fpColors.surfaceContainerLowest,
    alignItems: 'center',
    gap: 10,
  },
  confirmIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: `${fpColors.error}14`,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 4,
  },
  confirmTitle: {
    fontFamily: 'Inter_700Bold',
    fontSize: 18,
    color: fpColors.error,
    textAlign: 'center',
  },
  confirmBody: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    lineHeight: 18,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
  },
  confirmActions: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'stretch',
    width: '100%',
    marginTop: 8,
  },
  confirmSecondary: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    justifyContent: 'center',
    alignItems: 'center',
  },
  confirmSecondaryLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  confirmDanger: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: fpColors.error,
  },
  confirmDangerLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onPrimary,
  },
});
